import React, { FC, useEffect, useState } from 'react';
import classNames from 'classnames';

import Button from '../../../common/button';
import ModalWrap from '../../../common/modal-wrap';
import { CloseCrossBtn } from '../../../icons/close-cross-btn';
import {
  DPDG_SIZE_SLIDER_MAX,
  DPDG_SIZE_SLIDER_MIN,
  mapBallSizeToSliderStep,
  mapSliderStepToBallSize,
} from '../constants';

interface IBallSettingsModalProps {
  isShow: boolean;
  onClose: () => void;
  onApply: (settings: { color: string; size: number; speed: number }) => void;
  initialColor: string;
  initialSize: number;
  initialSpeed: number;
}

const tabs: Array<{ key: 'color' | 'size' | 'speed'; label: string }> = [
  { key: 'color', label: 'Цвет' },
  { key: 'size', label: 'Размер' },
  { key: 'speed', label: 'Скорость' },
];

const BallSettingsModal: FC<IBallSettingsModalProps> = ({
  isShow,
  onClose,
  onApply,
  initialColor,
  initialSize,
  initialSpeed,
}) => {
  const [activeTab, setActiveTab] = useState<'color' | 'size' | 'speed'>('color');
  const [color, setColor] = useState(initialColor);
  const [sizeStep, setSizeStep] = useState(() => mapBallSizeToSliderStep(initialSize));
  const [speed, setSpeed] = useState(initialSpeed);

  useEffect(() => {
    if (!isShow) {
      return;
    }

    setActiveTab('color');
    setColor(initialColor);
    setSizeStep(mapBallSizeToSliderStep(initialSize));
    setSpeed(initialSpeed);
  }, [isShow, initialColor, initialSize, initialSpeed]);

  const handleApply = () => {
    const nextSize = mapSliderStepToBallSize(sizeStep);
    onApply({ color, size: nextSize, speed });
  };

  const sizeInPixels = mapSliderStepToBallSize(sizeStep);

  return (
    <ModalWrap isShow={isShow}>
      <div className="relative flex w-[min(560px,92vw)] flex-col gap-5 rounded-[28px] bg-white p-6 shadow-shadowPopup">
        <button
          type="button"
          onClick={onClose}
          className="absolute right-5 top-5 flex h-9 w-9 items-center justify-center rounded-full bg-white text-[#DEC05E] shadow-[0_2px_8px_rgba(0,0,0,0.08)] transition-colors hover:bg-white/80"
          aria-label="Закрыть модальное окно"
        >
          <CloseCrossBtn width={18} height={18} fill="#DEC05E" fillBg="#DEC05E" />
        </button>

        <div className="pr-10">
          <h2 className="font-comfortaa text-lg font-bold text-blackBase lg:text-xl">Настройка шара</h2>
          <p className="mt-1.5 text-xs text-greyBase">
            Подберите цвет, размер и скорость движения для комфортной работы с инструментом.
          </p>
        </div>

        <div className="flex flex-wrap gap-2">
          {tabs.map((tab) => (
            <Button
              key={tab.key}
              type={activeTab === tab.key ? 'black' : 'grey'}
              onClick={() => setActiveTab(tab.key)}
              className={classNames(
                '!w-auto !text-base !leading-none transition-colors',
                activeTab === tab.key
                  ? '!bg-blackBase !text-white shadow-[0_4px_12px_rgba(17,16,38,0.18)]'
                  : '!bg-grey2 !text-blackBase hover:!bg-grey3',
              )}
            >
              {tab.label}
            </Button>
          ))}
        </div>

        <div className="min-h-[200px]">
          {activeTab === 'color' && (
            <div className="flex flex-col items-center gap-4 py-6">
              <div className="relative flex h-32 w-32 items-center justify-center">
                <span
                  className="block h-28 w-28 rounded-full border border-grey2 shadow-[inset_0_2px_8px_rgba(0,0,0,0.12)]"
                  style={{ backgroundColor: color }}
                />
                <input
                  aria-label="Выберите цвет шара"
                  type="color"
                  value={color}
                  onChange={(event) => setColor(event.target.value)}
                  className="absolute inset-0 h-full w-full cursor-pointer opacity-0"
                />
              </div>
              <div className="font-comfortaa text-xs uppercase tracking-[0.08em] text-greyBase">
                {color.toUpperCase()}
              </div>
            </div>
          )}

          {activeTab === 'size' && (
            <div className="flex h-full flex-col items-center gap-6 py-4">
              <div className="font-comfortaa text-sm font-semibold text-blackBase">Размер: {sizeInPixels}</div>

              <input
                type="range"
                min={DPDG_SIZE_SLIDER_MIN}
                max={DPDG_SIZE_SLIDER_MAX}
                step={1}
                value={sizeStep}
                onChange={(event) => setSizeStep(Number(event.target.value))}
                className="h-2 w-full cursor-pointer appearance-none rounded-full bg-grey2 accent-blackBase"
              />

              <div className="relative flex items-center justify-center h-[140px] w-full">
                <div
                  className="rounded-full shadow-[0_4px_8px_rgba(0,0,0,0.25)] border border-grey2 transition-all duration-200"
                  style={{
                    backgroundColor: color,
                    width: `${sizeInPixels}px`,
                    height: `${sizeInPixels}px`,
                  }}
                />
              </div>
            </div>
          )}

          {activeTab === 'speed' && (
            <div className="flex h-full flex-col gap-6 py-4">
              <div className="font-comfortaa text-sm font-semibold text-blackBase">Скорость: {speed}</div>

              <input
                type="range"
                min={1}
                max={40}
                value={speed}
                onChange={(event) => setSpeed(Number(event.target.value))}
                className="h-2 w-full cursor-pointer appearance-none rounded-full bg-grey2 accent-blackBase"
              />

              <div className="flex justify-between text-xs text-greyBase">
                <span>Медленнее</span>
                <span>Быстрее</span>
              </div>

              <div className="relative w-full h-[100px] overflow-hidden mt-4 bg-grey2/10 rounded-md">
                <div
                  className="absolute top-1/2 -translate-y-1/2 rounded-full animate-moveBall"
                  style={{
                    backgroundColor: color,
                    width: '40px',
                    height: '40px',
                    animationDuration: `${Math.max(0.1, 5 / speed).toFixed(2)}s`,
                    animationTimingFunction: 'linear',
                  }}
                />
              </div>

              <style>
                {`
        @keyframes moveBall {
          0% { left: 0%; }
          100% { left: calc(100% - 40px); }
        }
        .animate-moveBall {
          animation-name: moveBall;
          animation-iteration-count: infinite;
          animation-direction: alternate;
        }
      `}
              </style>
            </div>
          )}
        </div>

        <div className="flex justify-end gap-2 pt-2">
          <Button type="grey" className="max-w-[160px] !text-base" onClick={onClose}>
            Отмена
          </Button>
          <Button className="max-w-[160px] !text-base" type="primary" onClick={handleApply}>
            ОК
          </Button>
        </div>
      </div>
    </ModalWrap>
  );
};

export default BallSettingsModal;
